/*
 * @Copyright 2026. Institute for Future Intelligence, Inc.
 *
 * Global (velocity-rescale) thermostats, shared by the crystal MD engine
 * (CrystalDynamics) and the molecular lab engine (MolecularDynamics, where
 * 'berendsen' means its historical instant rescale, Δt/τ = 1, and the
 * per-atom Langevin flag takes precedence over either).
 *
 * Two are offered:
 *
 *   Berendsen — weak-coupling velocity rescaling. Robust and fast at
 *   driving a system to a target temperature, but it suppresses the
 *   kinetic-energy fluctuations, so it does NOT sample the true
 *   canonical (NVT) ensemble.
 *
 *   CSVR — canonical sampling through velocity rescaling (Bussi,
 *   Donadio & Parrinello, J. Chem. Phys. 126, 014101, 2007). It is the
 *   Berendsen rescale plus a stochastic term tuned so the kinetic
 *   energy relaxes toward the canonical χ² distribution rather than to
 *   a fixed value. Same robustness and ease as Berendsen, but it
 *   reproduces the correct kinetic-energy fluctuations and removes the
 *   "flying ice cube" bias.
 *
 * Berendsen lives inline in CrystalDynamics.setTemperature() — it is a
 * one-line rescale. This file holds the CSVR machinery, which needs
 * gamma-distributed noise.
 */

/** Stable ids — stored on ProjectState.thermostat. */
export type ThermostatType = 'berendsen' | 'csvr';

/** Registry for the UI selector, ordered with the default first. */
export const THERMOSTATS: { id: ThermostatType; name: string }[] = [
  { id: 'berendsen', name: 'Berendsen (weak coupling)' },
  { id: 'csvr', name: 'CSVR (canonical)' },
];

/** Normalize a possibly-undefined / legacy stored value to a valid id. */
export const resolveThermostat = (value: string | undefined | null): ThermostatType =>
  value === 'csvr' ? 'csvr' : 'berendsen';

// --- random-number machinery -----------------------------------------

/** One standard-normal deviate (Box-Muller). Self-contained on purpose. */
let spareGaussian: number | null = null;
const gaussian = (): number => {
  if (spareGaussian !== null) {
    const g = spareGaussian;
    spareGaussian = null;
    return g;
  }
  let u = 0;
  let v = 0;
  let s = 0;
  do {
    u = 2 * Math.random() - 1;
    v = 2 * Math.random() - 1;
    s = u * u + v * v;
  } while (s >= 1 || s === 0);
  const f = Math.sqrt((-2 * Math.log(s)) / s);
  spareGaussian = v * f;
  return u * f;
};

/**
 * A Gamma(shape, 1) deviate for shape ≥ 1, by the Marsaglia-Tsang
 * method — exact, with no rejection-rate blow-up at large shape.
 */
const gammaDeviate = (shape: number): number => {
  const d = shape - 1 / 3;
  const c = 1 / Math.sqrt(9 * d);
  for (;;) {
    let x = 0;
    let v = 0;
    do {
      x = gaussian();
      v = 1 + c * x;
    } while (v <= 0);
    v = v * v * v;
    const u = Math.random();
    const x2 = x * x;
    if (u < 1 - 0.0331 * x2 * x2) return d * v;
    if (Math.log(u) < 0.5 * x2 + d * (1 - v + Math.log(v))) return d * v;
  }
};

/**
 * Sum of `n` squared standard-normal deviates — i.e. a χ²(n) variate.
 * For n > 1 this is drawn as a gamma deviate rather than by summing n
 * squares, so the cost is O(1) in n. Follows Bussi's reference code.
 */
const sumNoises = (n: number): number => {
  if (n <= 0) return 0;
  if (n === 1) {
    const g = gaussian();
    return g * g;
  }
  if (n % 2 === 0) return 2 * gammaDeviate(n / 2);
  const g = gaussian();
  return 2 * gammaDeviate((n - 1) / 2) + g * g;
};

/**
 * CSVR core. Given the current kinetic energy `ke`, the target average
 * kinetic energy `targetKE`, the number of degrees of freedom `dof`,
 * and the coupling ratio `dtOverTau = Δt/τ`, return the resampled
 * kinetic energy for this step (Bussi-Donadio-Parrinello 2007, Eq. A7).
 * The caller turns it into a velocity scale factor √(keNew / ke).
 */
export const csvrNewKineticEnergy = (ke: number, targetKE: number, dof: number, dtOverTau: number): number => {
  if (ke <= 0 || dof < 1) return ke;
  // c = exp(-Δt/τ): the fraction of the old KE retained deterministically.
  const c = Math.exp(-Math.max(dtOverTau, 0));
  const r1 = gaussian();
  const noise = sumNoises(dof - 1) + r1 * r1;
  const keNew =
    ke + (1 - c) * ((targetKE * noise) / dof - ke) + 2 * r1 * Math.sqrt(((c * (1 - c) * targetKE) / dof) * ke);
  return keNew > 0 ? keNew : 0;
};
